import { Chip, FilterLabel, Segment } from "@/components/ui"
import { ANSICHTEN, MED_KURZ, gleich, type AuswertungsDaten, type Filter } from "@/lib/auswertung"
import { nf0 } from "@/lib/format"

interface Props {
  d: AuswertungsDaten
  f: Filter
  setF: (f: Filter) => void
  anzahlTyp: number[]
}

function umgeschaltet(menge: Set<number>, wert: number): Set<number> {
  const neu = new Set(menge)
  if (neu.has(wert)) neu.delete(wert)
  else neu.add(wert)
  return neu
}

/** Eine Filterzeile über allem, was sie betrifft; jeder Filter wirkt auf alle Bausteine. */
export function FilterLeiste({ d, f, setF, anzahlTyp }: Props) {
  const aktiveAnsicht = ANSICHTEN.find((a) => {
    const z = a.setze(d)
    return gleich(z.med, f.med) && gleich(z.geb, f.geb) && gleich(z.anf, f.anf)
  })
  const gruppe = "flex min-w-0 flex-wrap items-center gap-1.5"
  return (
    <nav aria-label="Filter" className="border-y border-hair/10 bg-page xl:sticky xl:top-0 xl:z-10">
      <div className="mx-auto flex max-w-[1260px] flex-wrap items-start gap-x-5 gap-y-2.5 px-4 py-3">
        <div className={gruppe}>
          <FilterLabel>Ansicht</FilterLabel>
          <Segment
            label="Ansicht"
            werte={ANSICHTEN.map((a) => ({ id: a.id, name: a.name, titel: a.titel }))}
            wert={aktiveAnsicht?.id ?? null}
            onChange={(id) => setF({ ...f, ...ANSICHTEN.find((a) => a.id === id)!.setze(d) })}
          />
        </div>
        <div className={gruppe} role="group" aria-label="Jahr">
          <FilterLabel>Jahr</FilterLabel>
          {d.meta.jahre.map((y) => (
            <Chip key={y} an={f.jahre.has(y)} onClick={() => setF({ ...f, jahre: umgeschaltet(f.jahre, y) })}>
              {y}
            </Chip>
          ))}
        </div>
        <div className={gruppe} role="group" aria-label="Medium">
          <FilterLabel>Medium</FilterLabel>
          {d.dims.med.map((m, i) => (
            <Chip key={m} an={f.med.has(i)} onClick={() => setF({ ...f, med: umgeschaltet(f.med, i) })}>
              {MED_KURZ[m] ? `${m} (${MED_KURZ[m]})` : m}
            </Chip>
          ))}
        </div>
        <div className={gruppe} role="group" aria-label="Gebinde">
          <FilterLabel>Gebinde</FilterLabel>
          {d.dims.geb.map((g, i) => (
            <Chip key={g} an={f.geb.has(i)} onClick={() => setF({ ...f, geb: umgeschaltet(f.geb, i) })}>
              {g}
            </Chip>
          ))}
        </div>
        <div className={gruppe} role="group" aria-label="Anfeuchtung Soll">
          <FilterLabel>Anfeuchtung Soll</FilterLabel>
          {d.dims.anf.map((a, i) => (
            <Chip key={a} an={f.anf.has(i)} onClick={() => setF({ ...f, anf: umgeschaltet(f.anf, i) })}>
              {a === "ohne" ? "ohne" : `${a} %`}
            </Chip>
          ))}
        </div>
        <div className={gruppe}>
          <FilterLabel htmlFor="f-typ">NC-Typ</FilterLabel>
          <select
            id="f-typ"
            value={f.typ}
            onChange={(e) => setF({ ...f, typ: Number(e.target.value) })}
            className="max-w-full rounded-lg border border-hair/10 bg-surface px-2 py-1.5 text-[12.5px] font-medium text-ink"
          >
            <option value={-1}>Alle NC-Typen</option>
            {d.dims.typ.map((t, i) => (
              <option key={t} value={i}>{`${t}  (${nf0.format(anzahlTyp[i] ?? 0)})`}</option>
            ))}
          </select>
        </div>
        <div className={gruppe}>
          <FilterLabel>Kennwert</FilterLabel>
          <Segment
            label="Kennwert"
            werte={[{ id: "mean", name: "Mittelwert" }, { id: "median", name: "Median" }]}
            wert={f.kw}
            onChange={(kw) => setF({ ...f, kw })}
          />
          <label className="inline-flex cursor-pointer items-center gap-1.5 text-[12.5px] text-ink2">
            <input
              type="checkbox"
              checked={f.ausr}
              onChange={(e) => setF({ ...f, ausr: e.target.checked })}
              className="h-[15px] w-[15px] accent-[rgb(var(--accent))]"
            />
            Ausreißer einbeziehen
          </label>
        </div>
      </div>
    </nav>
  )
}
